import React, { useState, useEffect, useRef } from "react";
import { DayPicker } from "react-day-picker";
import { pt } from "react-day-picker/locale";
import { FaEye } from "react-icons/fa";

import api from '../../config/axios';
import OperacaoDetalhesModal from './modals/Detalhes';

const Operacoes = () => {
    const [error, setError] = useState(null);
    const [selectedOperacao, setSelectedOperacao] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const initialColumns = [
        { id: "agendado", title: "Agendado", tasks: [] },
        { id: "confirmado", title: "Confirmado", tasks: [] },
        { id: "aguardando", title: "Aguardando", tasks: [] },
        { id: "em-atendimento", title: "Em Atendimento", tasks: [] },
        { id: "pago", title: "Pago", tasks: [] },
        { id: "finalizado", title: "Finalizado", tasks: [] },
        { id: "cancelado", title: "Cancelado", tasks: [] },
        { id: "faltou", title: "Faltou", tasks: [] }
    ];

    const [columns, setColumns] = useState(initialColumns);
    const [selectedDay, setSelectedDay] = useState(new Date());
    const lastSelectedDay = useRef(selectedDay);

    const fetchAgendamentos = async () => {
        const data = selectedDay.toLocaleDateString();
        const [dia, mes, ano] = data.split("/");

        try {
            const response = await api.get(`/agendamentos_operacoes?data=${ano}-${mes}-${dia}`);

            const agendamentos = response.data.agendamentos;

            const updatedColumns = initialColumns.map(col => ({ ...col, tasks: [] }));

            agendamentos.forEach(agendamento => {
                const index = updatedColumns.findIndex(col => col.id === agendamento.status.replace(' ', '-'));
                if (index !== -1) {
                    agendamento.servicos.forEach((servico, servicoIndex) => {
                        updatedColumns[index].tasks.push({
                            id: `${agendamento.id_agendamento}-${servicoIndex}`,
                            agendamento_id: agendamento.id_agendamento,
                            nomeCliente: agendamento.cliente.nome,
                            nomeColaborador: servico.colaborador.nome,
                            nomeServico: servico.servico.nome,
                            horaInicio: servico.hora_inicio ? servico.hora_inicio.substring(0, 5) : '00:00',
                            horaTermino: servico.hora_termino ? servico.hora_termino.substring(0, 5) : '00:00',
                            idComanda: agendamento.id_comanda || null,
                            status: agendamento.status,
                            observacao: agendamento.observacao,
                            cliente: agendamento.cliente,
                            colaborador: servico.colaborador,
                            servico: servico.servico,
                            agendamento: agendamento 
                        });
                    });
                }
            });

            setColumns(updatedColumns);

        } catch (err) {
            setError('Erro ao carregar a lista de operações.');
        }
    };

    useEffect(() => {
        if (selectedDay) {
            if (lastSelectedDay.current.toLocaleDateString() !== selectedDay.toLocaleDateString()) {
                lastSelectedDay.current = selectedDay;
                fetchAgendamentos();
            }
        }
    }, [selectedDay]);

    useEffect(() => {
        fetchAgendamentos();
    }, []);

    return (
        <div>
            <div className="container-fluid">
                <div className="card">
                    <div className="card-body">
                        <div className="row mt-3">
                            <div className="col-12">
                                <h4 className="fw-bold">Operações</h4>
                            </div>
                        </div>
                        <div className="row mt-3">
                            <div className="d-lg-flex d-block align-items-start">
                                <div className="me-3">
                                    <DayPicker
                                        mode="single"
                                        locale={pt}
                                        selected={selectedDay}
                                        onSelect={setSelectedDay}
                                    />
                                </div>
                                <div className="flex-grow-1 mt-lg-0 mt-3" style={{ overflowX: "auto", maxWidth: "100%" }}>
                                    <div
                                        style={{
                                            display: "flex",
                                            gap: "10px",
                                            paddingBottom: "10px",
                                            minWidth: "100%",
                                        }}
                                    >
                                        {columns.map((col) => (
                                            <div key={col.id} style={{ minWidth: "350px", flexShrink: 0 }}>
                                                <Column column={col} setSelectedOperacao={setSelectedOperacao} setIsModalOpen={setIsModalOpen} />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {isModalOpen && (
                <OperacaoDetalhesModal
                    operacao={selectedOperacao}
                    onClose={() => setIsModalOpen(false)}
                />
            )}
        </div>
    );
}

function Column({ column, setSelectedOperacao, setIsModalOpen }) {
    return (
        <div>
            <h5 className="text-center fw-bold">{column.title}</h5>
            <div
                style={{
                    padding: "10px",
                    background: "#f8f9fa",
                    borderRadius: "8px",
                    height: "70vh",
                    overflowY: "auto",
                }}
            >
                {column.tasks.length === 0 ? (
                    <div style={{ textAlign: "center", color: "#666", padding: "10px" }}>Vazio</div>
                ) : (
                    column.tasks.map((task) => (
                        <TaskCard
                            key={task.id} 
                            task={task} 
                            columnId={column.id} 
                            setSelectedOperacao={setSelectedOperacao}
                            setIsModalOpen={setIsModalOpen}
                        />
                    ))
                )}
            </div>
        </div>
    );
}

const columnBorderColors = {
    agendado: "#036d61",
    confirmado: "#17a2b8",
    aguardando: "#ffc107",
    emAtendimento: "#28a745",
    finalizado: "#0056b3", 
    pago: "#ff8c69",
    cancelado: "#9b9b9b",
    faltou: "#6c757d"
};

function TaskCard({ task, isOverlay, columnId, setSelectedOperacao, setIsModalOpen }) {
    if (!task) return null;

    if (columnId == 'em-atendimento') columnId = 'emAtendimento';
    const borderColor = columnBorderColors[columnId] || "#ccc";

    const handleOpenModal = (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        const operacao = {
            id_agendamento: task.agendamento_id,
            id_comanda: task.idComanda,
            hora_inicio: task.horaInicio,
            hora_termino: task.horaTermino,
            status: task.status,
            observacao: task.observacao,
            servico: task.servico,
            cliente: task.cliente,
            colaborador: task.colaborador,
            agendamento: task.agendamento 
        };
        setSelectedOperacao(operacao);
        setIsModalOpen(true);
    };

    return (
        <div
            style={{
                padding: "10px",
                background: "#fff",
                borderRadius: "4px",
                marginBottom: "8px",
                boxShadow: isOverlay ? "0px 5px 15px rgba(0,0,0,0.3)" : "0 2px 4px rgba(0,0,0,0.1)",
                transform: isOverlay ? "scale(1.05)" : "none",
                transition: "transform 250ms ease",
                border: `3px solid ${borderColor}`
            }}
        >
            <div className="d-flex flex-column" style={{ borderBottom: '1px solid' + borderColor }}>
                <strong>{task.nomeCliente}</strong>
                <span>{task.nomeColaborador}</span>
                <span>{task.nomeServico}</span>
                <span className="text-muted">{task.horaInicio} - {task.horaTermino}</span>
                {task.idComanda ? <span>Comanda N° {task.idComanda}</span> : null}
            </div>
            {setSelectedOperacao && setIsModalOpen && (
                <div className="text-end mt-2">
                    <button 
                        className="btn btn-sm btn-custom-primary" 
                        onClick={handleOpenModal}
                        style={{ cursor: 'pointer' }}
                    >   
                        <FaEye /> Detalhes
                    </button>
                </div>
            )}
        </div>
    );
}

export default Operacoes; 